import React, { useState, useEffect, useMemo, useCallback } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import BuildIcon from "@mui/icons-material/Build";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ImageIcon from "@mui/icons-material/Image";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import RightworkSelect from "../../../components/shared/RightworkSelect";
import {
  getRightworkAPI,
  postRightworkAPI,
  uploadFile,
} from "../requests/utilities/requests";
import {
  API_get_store_employees_for_assignment,
  API_get_ticket_issue_types,
  API_ops_assistant_confirm_action,
} from "../../../constants";
import { toast } from "react-toastify";
import {
  MENU_OPTIONS,
  getDefaultStatusOptions,
  resolveStatusLabel,
  withLegacyMenus,
} from "../maintenance/MaintenanceBadges";

const VIDEO_EXTENSIONS = new Set(["mp4", "mov", "webm", "m4v", "avi", "hevc"]);
// HEIC/HEIF are accepted for upload but the backend re-encodes them to JPEG
// (browsers other than Safari can't render them); until that runs they show
// as a generic file rather than a broken image.
const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp", "svg"]);

function getFileExtension(file) {
  if (file.extension) return String(file.extension).toLowerCase();
  const source = file.name || file.presigned_url || "";
  return source.split(".").pop()?.split("?")[0]?.toLowerCase() || "";
}

function getFileKind(file) {
  const ext = getFileExtension(file);
  if (VIDEO_EXTENSIONS.has(ext)) return "video";
  if (IMAGE_EXTENSIONS.has(ext)) return "image";
  if (ext === "pdf") return "pdf";
  return "other";
}

// scheduled_time round-trips through <input type="datetime-local">,
// which deals in tz-naive "YYYY-MM-DDTHH:MM" strings interpreted in
// the BROWSER'S local timezone. The backend stores the field as a UTC
// datetime, so we have to convert on both ends or vendor appointments
// drift by hours for managers operating outside the server's tz.
//
// Matches MaintenanceTicketDrawer's `new Date(iso)` / `toISOString()`
// pattern, which is consistent across the rest of the maintenance UI.
function isoToLocalInputValue(raw) {
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function localInputValueToIso(local) {
  if (!local) return "";
  // ``new Date("YYYY-MM-DDTHH:MM")`` is parsed by the browser as
  // local time (per HTML spec for the datetime-local input format).
  // ``.toISOString()`` then returns the same instant in UTC with a
  // Z suffix, which Django's parse_datetime keeps tz-aware.
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

const EMPTY_EQUIPMENT = { manufacturer: "", model_number: "", serial_number: "", plate_notes: "" };
const EQUIPMENT_FIELDS = [
  ["manufacturer", "Manufacturer"],
  ["model_number", "Model number"],
  ["serial_number", "Serial number"],
];
const hasEquipmentValues = (eq) => Object.values(eq || {}).some((v) => v && String(v).trim());

// Optional equipment block shared by the create and edit cards. Shown when
// the selected issue type is flagged by the company, or when values exist.
function EquipmentFields({ equipment, setEquipment, show }) {
  if (!show && !hasEquipmentValues(equipment)) return null;
  return (
    <div className="rounded-md border border-violet-200 bg-white p-2 space-y-2">
      <p className="text-xs font-medium text-slate-700">
        Equipment details <span className="font-normal text-slate-400">(optional, from the data plate)</span>
      </p>
      <div className="grid grid-cols-3 gap-2">
        {EQUIPMENT_FIELDS.map(([key, label]) => (
          <input
            key={key}
            type="text"
            value={equipment[key] || ""}
            onChange={(e) => setEquipment((prev) => ({ ...prev, [key]: e.target.value }))}
            maxLength={255}
            className={inputClasses}
            placeholder={label}
            aria-label={label}
          />
        ))}
      </div>
      <input
        type="text"
        value={equipment.plate_notes || ""}
        onChange={(e) => setEquipment((prev) => ({ ...prev, plate_notes: e.target.value }))}
        maxLength={1000}
        className={inputClasses}
        placeholder="Other data plate info"
        aria-label="Other data plate info"
      />
    </div>
  );
}

const EMPTY_MENU = { go_live_date: "", menus: [], online_ordering: null, replaces_item: "", kill_date: "" };

// Stored menu details overlaid on the empty shape; menus is always a list.
function menuState(...sources) {
  const merged = Object.assign({ ...EMPTY_MENU }, ...sources.map((m) => m || {}));
  return withLegacyMenus(merged);
}

const chipClasses = (selected) =>
  `px-2.5 py-1.5 rounded-md border text-xs font-medium transition-colors ${
    selected
      ? "border-violet-600 bg-violet-50 text-violet-700"
      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
  }`;

// Optional menu block shared by the create and edit cards, on menu tickets.
// Same questions and wording as the ticket drawer.
function MenuFields({ menu, setMenu, idPrefix, menuOptions }) {
  const set = (key, value) => setMenu((prev) => ({ ...prev, [key]: value }));
  const menus = Array.isArray(menu.menus) ? menu.menus : [];
  return (
    <div className="rounded-md border border-violet-200 bg-white p-2 space-y-2">
      <p className="text-xs font-medium text-slate-700">
        Menu details <span className="font-normal text-slate-400">(optional)</span>
      </p>
      <label className="block text-xs text-slate-500">
        Target go-live date
        <input
          type="date"
          value={menu.go_live_date || ""}
          onChange={(e) => set("go_live_date", e.target.value)}
          className={inputClasses}
        />
      </label>
      <div>
        <p className="text-xs text-slate-500" id={`${idPrefix}-menus-label`}>
          Which menus need to be updated
        </p>
        <div className="mt-1 flex flex-wrap gap-1.5" role="group" aria-labelledby={`${idPrefix}-menus-label`}>
          {menuOptions.map((opt) => {
            const selected = menus.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                aria-pressed={selected}
                className={chipClasses(selected)}
                onClick={() =>
                  set("menus", selected ? menus.filter((v) => v !== opt.value) : [...menus, opt.value])
                }
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        {menu.menus_other && (
          <p className="text-xs text-slate-500 mt-1">Other menus: {menu.menus_other}</p>
        )}
      </div>
      <div>
        <p className="text-xs text-slate-500" id={`${idPrefix}-online-label`}>
          Will this item be available for online ordering?
        </p>
        <div className="mt-1 flex gap-1.5" role="group" aria-labelledby={`${idPrefix}-online-label`}>
          {[
            { value: true, label: "Yes" },
            { value: false, label: "No" },
          ].map((opt) => {
            const selected = menu.online_ordering === opt.value;
            return (
              <button
                key={opt.label}
                type="button"
                aria-pressed={selected}
                className={`flex-1 ${chipClasses(selected)}`}
                onClick={() => set("online_ordering", selected ? null : opt.value)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      <label className="block text-xs text-slate-500">
        What item does this replace?
        <input
          type="text"
          value={menu.replaces_item || ""}
          onChange={(e) => set("replaces_item", e.target.value)}
          maxLength={255}
          className={inputClasses}
          placeholder="e.g. Classic Burger (leave blank if none)"
        />
      </label>
      <label className="block text-xs text-slate-500">
        Kill date for the replaced item
        <input
          type="date"
          value={menu.kill_date || ""}
          onChange={(e) => set("kill_date", e.target.value)}
          className={inputClasses}
        />
      </label>
    </div>
  );
}

// Company issue types and priority labels for one category. The proposal's
// snapshot is trusted only when it is for this category AND carries
// priorities (a proposal minted by the previous release has none); otherwise
// the current options are fetched and confirmation waits until they arrive.
function useCategoryOptions({ homeStore, category, snapshotCategory, snapshotIssueTypes, snapshotPriorities }) {
  const snapshotUsable =
    snapshotCategory === category && Array.isArray(snapshotPriorities) && snapshotPriorities.length > 0;
  const [fetched, setFetched] = useState(null);
  const [status, setStatus] = useState("ready"); // ready | loading | error
  const [retryKey, setRetryKey] = useState(0);
  useEffect(() => {
    if (snapshotUsable || !homeStore || !category) {
      setStatus("ready");
      return undefined;
    }
    let cancelled = false;
    setStatus("loading");
    (async () => {
      try {
        const res = await getRightworkAPI(API_get_ticket_issue_types, { store_id: homeStore, category });
        const payload = res.data?.redux?.payload;
        if (cancelled) return;
        // Require priorities: a backend still on the previous release
        // answers without them, and the built-in wording must not be shown.
        if (payload?.priorities?.length) {
          setFetched({
            category,
            issue_types: payload.issue_types || [],
            priorities: payload.priorities,
            menu_options: payload.menu_options || [],
          });
          setStatus("ready");
        } else {
          setStatus("error");
        }
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, [homeStore, category, snapshotUsable, retryKey]);
  const fetchedForCategory = fetched?.category === category ? fetched : null;
  const issueTypeOptions = useMemo(() => {
    if (snapshotUsable && Array.isArray(snapshotIssueTypes) && snapshotIssueTypes.length > 0) return snapshotIssueTypes;
    if (fetchedForCategory?.issue_types?.length) return fetchedForCategory.issue_types;
    return FALLBACK_ISSUE_TYPES[category] || FALLBACK_ISSUE_TYPES.maintenance;
  }, [snapshotUsable, snapshotIssueTypes, fetchedForCategory, category]);
  const priorityOptions = useMemo(() => {
    if (snapshotUsable) return snapshotPriorities;
    if (fetchedForCategory?.priorities?.length) return fetchedForCategory.priorities;
    return PRIORITY_OPTIONS;
  }, [snapshotUsable, snapshotPriorities, fetchedForCategory]);
  // Menu choices aren't snapshotted; use fetched ones when present.
  const menuOptions = fetchedForCategory?.menu_options?.length ? fetchedForCategory.menu_options : MENU_OPTIONS;
  return {
    issueTypeOptions,
    priorityOptions,
    menuOptions,
    pending: !snapshotUsable && !fetchedForCategory,
    status,
    retry: () => setRetryKey((k) => k + 1),
  };
}

const CATEGORY_OPTIONS = [
  { value: "maintenance", label: "Maintenance" },
  { value: "it", label: "IT" },
  { value: "marketing", label: "Marketing" },
  { value: "menu", label: "Menu" },
];

// Fallback issue-type list for each category, used only if the
// proposal summary didn't include the company's resolved options
// (e.g. legacy proposal). The backend's get_issue_types_for_company
// is the source of truth at confirm time and will reject anything
// not in the company-configured set.
const FALLBACK_ISSUE_TYPES = {
  maintenance: [
    { value: "electrical_lighting", label: "Electrical/Lighting" },
    { value: "plumbing", label: "Plumbing" },
    { value: "hvac", label: "HVAC" },
    { value: "equipment", label: "Equipment" },
    { value: "furniture", label: "Furniture" },
    { value: "cleaning", label: "Cleaning" },
    { value: "safety", label: "Safety" },
    { value: "damage", label: "Damage" },
    { value: "other", label: "Other" },
  ],
  it: [
    { value: "hardware", label: "Hardware" },
    { value: "software", label: "Software" },
    { value: "network", label: "Network" },
    { value: "access_permissions", label: "Access/Permissions" },
    { value: "printer", label: "Printer" },
    { value: "phone_system", label: "Phone System" },
    { value: "other", label: "Other" },
  ],
  marketing: [
    { value: "signage", label: "Signage" },
    { value: "printing", label: "Printing" },
    { value: "social_media", label: "Social Media" },
    { value: "promotion", label: "Promotion" },
    { value: "branding", label: "Branding" },
    { value: "event", label: "Event" },
    { value: "other", label: "Other" },
  ],
  menu: [
    { value: "new_item", label: "New Item" },
    { value: "price_change", label: "Price Change" },
    { value: "text_edit", label: "Text Edit (Descriptions/Names/etc)" },
    { value: "remove_item", label: "Remove Item" },
    { value: "seasonal_changeover", label: "Seasonal Changeover" },
    { value: "allergen_dietary", label: "Allergen/Dietary Update" },
    { value: "toast_update", label: "Toast Update" },
    { value: "private_dining", label: "Private Dining Update" },
    { value: "other", label: "Other" },
  ],
};

function priorityChipClasses(priority) {
  switch (priority) {
    case "urgent":
      return "bg-red-100 text-red-700";
    case "high":
      return "bg-orange-100 text-orange-700";
    case "low":
      return "bg-gray-100 text-gray-600";
    default:
      return "bg-violet-100 text-violet-700";
  }
}

export default function TicketProposalCard({
  proposal,
  sessionUuid,
  homeStore,
  homeCompany,
  onStatusChange,
}) {
  const toolName = proposal.tool_name;
  const summary = proposal.summary || {};

  const [status, setStatus] = useState(proposal.status || "pending");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (
      (proposal.status === "cancelled" || proposal.status === "rejected") &&
      status === "pending"
    ) {
      setStatus("cancelled");
    }
  }, [proposal.status]);

  if (toolName === "comment_on_ticket") {
    return (
      <CommentTicketCard
        proposal={proposal}
        summary={summary}
        sessionUuid={sessionUuid}
        homeStore={homeStore}
        status={status}
        setStatus={setStatus}
        loading={loading}
        setLoading={setLoading}
        onStatusChange={onStatusChange}
      />
    );
  }

  if (toolName === "edit_ticket") {
    return (
      <EditTicketCard
        proposal={proposal}
        summary={summary}
        sessionUuid={sessionUuid}
        homeStore={homeStore}
        status={status}
        setStatus={setStatus}
        loading={loading}
        setLoading={setLoading}
        onStatusChange={onStatusChange}
      />
    );
  }

  // Default: create_ticket
  return (
    <CreateTicketCard
      proposal={proposal}
      summary={summary}
      sessionUuid={sessionUuid}
      homeStore={homeStore}
      homeCompany={homeCompany}
      status={status}
      setStatus={setStatus}
      loading={loading}
      setLoading={setLoading}
      onStatusChange={onStatusChange}
    />
  );
}

// -- Helpers shared across the three subcards ------------------------------

async function postConfirmAction({
  action,
  proposal,
  sessionUuid,
  homeStore,
  overrides,
  setStatus,
  setLoading,
  onStatusChange,
  errorMessage = "Failed to process action.",
}) {
  setLoading(true);
  try {
    const payload = {
      store_id: homeStore,
      session_uuid: sessionUuid,
      proposal_id: proposal.proposal_id,
      action,
    };
    if (action === "confirm" && overrides) {
      payload.overrides = overrides;
    }
    const res = await postRightworkAPI(
      API_ops_assistant_confirm_action,
      payload
    );
    if (res.status === 200) {
      const newStatus = action === "confirm" ? "confirmed" : "cancelled";
      setStatus(newStatus);
      onStatusChange?.(proposal.proposal_id, newStatus);
    } else {
      toast.error(errorMessage);
    }
  } catch (err) {
    toast.error(
      err?.response?.data?.system?.message ||
        err?.response?.data?.error ||
        errorMessage
    );
  }
  setLoading(false);
}

// -- create_ticket ---------------------------------------------------------

function CreateTicketCard({
  proposal,
  summary,
  sessionUuid,
  homeStore,
  homeCompany,
  status,
  setStatus,
  loading,
  setLoading,
  onStatusChange,
}) {
  const [title, setTitle] = useState(summary.ticket_title || "");
  const [description, setDescription] = useState(summary.description || "");
  const [category, setCategory] = useState(summary.category || "maintenance");
  const [issueType, setIssueType] = useState(summary.issue_type || "other");
  const [priority, setPriority] = useState(summary.priority || "normal");
  const [equipment, setEquipment] = useState({ ...EMPTY_EQUIPMENT, ...(summary.equipment_details || {}) });
  const [menu, setMenu] = useState(menuState(summary.menu_details));
  const [assigneeId, setAssigneeId] = useState(
    summary.assignee_employee_id || ""
  );
  const [employees, setEmployees] = useState([]);
  // file_uploads: [{ uuid, presigned_url, name, extension }]
  const [fileUploads, setFileUploads] = useState([]);
  const [isFileLoading, setIsFileLoading] = useState(false);

  // The proposal summary carries the company's resolved issue-type
  // options for the LLM-picked category. When the manager changes the
  // category in the form we don't have the company's options for the
  // new category, so we fall back to the standard list. A
  // company-custom issue type for the new category will get rejected
  // at confirm time with a clean error — better than offering a stale
  // dropdown that silently mismatches.
  const {
    issueTypeOptions,
    priorityOptions,
    menuOptions,
    pending: categoryOptionsPending,
    status: optionsStatus,
    retry: retryCategoryOptions,
  } = useCategoryOptions({
    homeStore,
    category,
    snapshotCategory: summary.category,
    snapshotIssueTypes: summary.issue_type_options,
    snapshotPriorities: summary.priority_options,
  });

  // Keep issueType inside the current category's options when the
  // manager flips the category — otherwise the dropdown shows the
  // previous value but no row, and the executor would reject it.
  useEffect(() => {
    const allowed = issueTypeOptions.map((it) => it.value);
    if (!allowed.includes(issueType)) {
      setIssueType(allowed.includes("other") ? "other" : allowed[0] || "other");
    }
  }, [issueTypeOptions, issueType]);

  // Fetch the eligible-assignee list. The endpoint is category-scoped
  // so non-managers without the matching override are filtered out;
  // we re-fetch when the manager flips the category dropdown so the
  // list stays in sync.
  // ``store_id`` is the API caller's session store (where the
  // manager is logged in — used for endpoint-level auth). The
  // ``ticket_store_id`` MUST be the proposal's target store so the
  // returned eligible-assignee list matches the store the ticket
  // will live at. For cross-store proposals (manager at Store A
  // creating a ticket for Store B), defaulting both to ``homeStore``
  // would seed the dropdown with employees from the wrong store —
  // those IDs would then fail the executor's category-eligibility
  // check at confirm time.
  const ticketStoreId = proposal?.store_id || homeStore;
  const fetchEmployees = useCallback(async () => {
    if (!homeStore || !ticketStoreId || !category) return;
    try {
      const params = {
        store_id: homeStore,
        ticket_store_id: ticketStoreId,
        category,
      };
      const res = await getRightworkAPI(API_get_store_employees_for_assignment, params);
      setEmployees(res.data?.redux?.payload?.employees || []);
    } catch {
      // Silent fail — picker just stays empty, manager can leave unassigned.
      setEmployees([]);
    }
  }, [homeStore, ticketStoreId, category]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Drop the selected assignee if the new category's eligible list
  // doesn't include them — prevents a confirm with an invalid id.
  // Skip while the list is still empty (initial mount before fetch
  // resolves, or a silent fetch failure) so we don't wipe an LLM-
  // supplied assignee_employee_id before the eligible list arrives.
  useEffect(() => {
    if (!assigneeId) return;
    if (employees.length === 0) return;
    if (!employees.some((e) => String(e.id) === String(assigneeId))) {
      setAssigneeId("");
    }
  }, [employees, assigneeId]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !homeCompany) {
      e.target.value = "";
      return;
    }
    setIsFileLoading(true);
    try {
      const result = await uploadFile(homeCompany, file, "maintenance_request");
      if (result?.uuid) {
        setFileUploads((prev) => [
          ...prev,
          {
            uuid: result.uuid,
            presigned_url: result.presigned_url || null,
            extension: result.extension || null,
            name: result.name || file.name,
          },
        ]);
      }
    } catch {
      toast.error("Failed to upload file.");
    } finally {
      setIsFileLoading(false);
      e.target.value = "";
    }
  };

  const handleRemoveFile = (uuidToRemove) => {
    setFileUploads((prev) => prev.filter((f) => f.uuid !== uuidToRemove));
  };

  const handleAction = (action) => {
    const overrides =
      action === "confirm"
        ? {
            title: title.trim(),
            description: description.trim(),
            category,
            issue_type: issueType,
            priority,
            equipment_details: equipment,
            ...(category === "menu" ? { menu_details: menu } : {}),
            assignee_employee_id: assigneeId
              ? Number(assigneeId)
              : null,
            file_upload_uuids: fileUploads.map((f) => f.uuid),
          }
        : undefined;
    return postConfirmAction({
      action,
      proposal,
      sessionUuid,
      homeStore,
      overrides,
      setStatus,
      setLoading,
      onStatusChange,
      errorMessage: "Failed to create ticket.",
    });
  };

  if (status === "confirmed") {
    return (
      <ConfirmedBanner
        icon={<CheckCircleIcon fontSize="small" className="text-green-600 mt-0.5 shrink-0" />}
        title="Ticket created"
        subtitle={title}
      />
    );
  }
  if (status === "cancelled" || status === "rejected") {
    return <CancelledBanner label="Cancelled" />;
  }

  const confirmDisabled =
    loading || isFileLoading || !title.trim() || !category || !issueType || categoryOptionsPending;

  return (
    <div className="my-2 max-w-md rounded-lg border border-violet-200 bg-violet-50">
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200 rounded-t-lg">
        <BuildIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">Create Ticket</span>
      </div>
      <div className="px-3 py-2 space-y-2">
        <FormField label="Title">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={255}
            className={inputClasses}
            placeholder="e.g. Walk-in cooler not cooling"
          />
        </FormField>

        <FormField label="Description" optional>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className={`${inputClasses} resize-none`}
            placeholder="Add details (when started, symptoms, who's affected)"
          />
        </FormField>

        <div className="flex gap-2">
          <FormField label="Category" className="flex-1">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className={inputClasses}
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Issue type" className="flex-1">
            <select
              value={issueType}
              onChange={(e) => setIssueType(e.target.value)}
              className={inputClasses}
            >
              {issueTypeOptions.map((it) => (
                <option key={it.value} value={it.value}>
                  {it.label}
                </option>
              ))}
            </select>
          </FormField>
        </div>

        {category === "maintenance" && <AssetLine asset={summary.asset} />}

        <EquipmentFields
          equipment={equipment}
          setEquipment={setEquipment}
          show={Boolean(
            summary.asset || issueTypeOptions.find((it) => it.value === issueType)?.equipment_details
          )}
        />

        {category === "menu" && (
          <MenuFields
            menu={menu}
            setMenu={setMenu}
            idPrefix={`ticket-menu-${proposal.proposal_id}`}
            menuOptions={menuOptions}
          />
        )}

        <div className="flex gap-2">
          <FormField label="Priority" className="flex-1" htmlFor={`ticket-priority-${proposal.proposal_id}`}>
            <RightworkSelect
              id={`ticket-priority-${proposal.proposal_id}`}
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              options={priorityOptions}
            />
          </FormField>
          <FormField label="Assignee" optional className="flex-1">
            <select
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              className={inputClasses}
            >
              <option value="">Unassigned</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.full_name ||
                    `${emp.first_name || ""} ${emp.last_name || ""}`.trim()}
                </option>
              ))}
            </select>
          </FormField>
        </div>

        <FormField label="Attachments" optional>
          <FileUploadField
            fileUploads={fileUploads}
            onUpload={handleFileUpload}
            onRemove={handleRemoveFile}
            isLoading={isFileLoading}
          />
        </FormField>
        {categoryOptionsPending && (
          <p className="text-xs text-amber-700">
            {optionsStatus === "error" ? (
              <>
                Couldn't load this category's options.{" "}
                <button
                  type="button"
                  className="underline font-medium"
                  onClick={retryCategoryOptions}
                >
                  Retry
                </button>
              </>
            ) : (
              "Loading this category's options…"
            )}
          </p>
        )}
      </div>
      <CardFooter
        loading={loading}
        confirmDisabled={confirmDisabled}
        confirmLabel="Create ticket"
        onCancel={() => handleAction("reject")}
        onConfirm={() => handleAction("confirm")}
      />
    </div>
  );
}

function FileUploadField({ fileUploads, onUpload, onRemove, isLoading }) {
  return (
    <div>
      {fileUploads.length > 0 && (
        <div className="mb-1.5 flex flex-wrap gap-1.5">
          {fileUploads.map((file) => {
            const kind = getFileKind(file);
            return (
              <div
                key={file.uuid}
                className="relative group w-[72px] h-[72px] rounded-md border border-slate-200 overflow-hidden bg-white cursor-pointer"
                onClick={() => {
                  if (file.presigned_url) {
                    window.open(file.presigned_url, "_blank", "noopener,noreferrer");
                  }
                }}
              >
                {!file.presigned_url ? (
                  <div className="w-full h-full flex items-center justify-center bg-slate-50">
                    <ImageIcon sx={{ color: "#94a3b8", fontSize: 22 }} />
                  </div>
                ) : kind === "video" ? (
                  <div className="w-full h-full flex items-center justify-center bg-slate-900">
                    <PlayCircleOutlineIcon sx={{ color: "white", fontSize: 28 }} />
                  </div>
                ) : kind === "image" ? (
                  <img
                    src={file.presigned_url}
                    alt={file.name || "Attachment"}
                    className="w-full h-full object-cover"
                  />
                ) : kind === "pdf" ? (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-slate-50 p-1">
                    <PictureAsPdfIcon sx={{ color: "#dc2626", fontSize: 22 }} />
                    <span className="mt-0.5 text-[9px] leading-tight text-slate-600 text-center break-all line-clamp-2">
                      {file.name || "PDF"}
                    </span>
                  </div>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-slate-50 p-1">
                    <InsertDriveFileIcon sx={{ color: "#94a3b8", fontSize: 22 }} />
                    <span className="mt-0.5 text-[9px] leading-tight text-slate-600 text-center break-all line-clamp-2">
                      {file.name || "File"}
                    </span>
                  </div>
                )}
                <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(file.uuid);
                    }}
                    sx={{
                      backgroundColor: "rgba(0,0,0,0.5)",
                      color: "white",
                      "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
                      width: 20,
                      height: 20,
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <label className="inline-flex items-center gap-1.5 text-violet-700 cursor-pointer hover:text-violet-800 text-sm">
        {isLoading ? (
          <CircularProgress size={14} sx={{ color: "#6d28d9" }} />
        ) : (
          <AttachFileIcon fontSize="small" />
        )}
        <span>{isLoading ? "Uploading..." : "Attach file"}</span>
        <input
          type="file"
          accept=".jpg,.jpeg,.png,.heic,.heif,.svg,.pdf,.mp4,.mov,.webm,.m4v,.avi,.hevc"
          className="hidden"
          onChange={onUpload}
          disabled={isLoading}
        />
      </label>
    </div>
  );
}

// -- edit_ticket -----------------------------------------------------------

function EditTicketCard({
  proposal,
  summary,
  sessionUuid,
  homeStore,
  status,
  setStatus,
  loading,
  setLoading,
  onStatusChange,
}) {
  const before = summary.before || {};
  const ticketCategory = before.category || "maintenance";
  const isMenuTicket = ticketCategory === "menu";
  // The category's status wording (menu relabels and hides Waiting: Vendor).
  // A ticket already on a hidden status keeps it listed.
  const statusOptions = useMemo(() => {
    const options = summary.status_options?.length
      ? summary.status_options
      : getDefaultStatusOptions(ticketCategory);
    const current = before.status;
    if (current && !options.some((o) => o.value === current)) {
      return [...options, { value: current, label: resolveStatusLabel(current) }];
    }
    return options;
  }, [summary.status_options, ticketCategory, before.status]);

  // Initialize each editable field from the proposal's overrides if
  // the LLM included them, else from the ticket's current value. This
  // way the manager sees the LLM's intended changes pre-filled but can
  // adjust before confirming.
  const [title, setTitle] = useState(
    summary.ticket_title_edit !== undefined && summary.ticket_title_edit !== null
      ? summary.ticket_title_edit
      : before.title || ""
  );
  const [description, setDescription] = useState(
    summary.description !== undefined && summary.description !== null
      ? summary.description
      : before.description || ""
  );
  // Capture the initial issue_type so we can detect "manager actually
  // changed it" at submit time. Always-sending issue_type would
  // re-run the executor's company-config validation against the
  // existing value, which fails for legacy tickets whose stored
  // issue_type is no longer in the company's current custom config —
  // blocking unrelated edits like status/priority/vendor.
  const initialIssueType =
    summary.issue_type || before.issue_type || "other";
  const [issueType, setIssueType] = useState(initialIssueType);
  const [statusValue, setStatusValue] = useState(summary.status || before.status || "new");
  const [priority, setPriority] = useState(summary.priority || before.priority || "normal");
  // Equipment: existing ticket values overlaid with what the assistant
  // proposed, so a guessed model/serial is visible and editable before
  // it is merged onto the ticket. Only sent when the manager changed it.
  const initialEquipment = useMemo(
    () => ({ ...EMPTY_EQUIPMENT, ...(before.equipment_details || {}), ...(summary.equipment_details || {}) }),
    [before.equipment_details, summary.equipment_details]
  );
  const [equipment, setEquipment] = useState(initialEquipment);
  // Menu: same overlay as equipment, only on menu tickets.
  const initialMenu = useMemo(
    () => menuState(before.menu_details, summary.menu_details),
    [before.menu_details, summary.menu_details]
  );
  const [menu, setMenu] = useState(initialMenu);
  // Distinguish ``null`` (LLM's explicit unassign intent — surfaces
  // through the proposal summary because tool_args.get(key, default)
  // only returns the default when the key is MISSING, not when it's
  // present with value None) from ``undefined`` (proposal didn't set
  // the field at all). Falling back to ``before.assignee_employee_id``
  // on null would silently drop the unassign and confirm the existing
  // assignee — exactly the bug surfaced in review.
  const initialAssigneeId = (() => {
    if (summary.assignee_employee_id === null) return "";
    if (summary.assignee_employee_id !== undefined) {
      return String(summary.assignee_employee_id);
    }
    // Defensive: proposals.py always populates this key, so this
    // branch shouldn't fire in practice.
    return before.assignee_employee_id
      ? String(before.assignee_employee_id)
      : "";
  })();
  const [assigneeId, setAssigneeId] = useState(initialAssigneeId);
  const [vendor, setVendor] = useState(
    summary.vendor !== undefined && summary.vendor !== null ? summary.vendor : before.vendor || ""
  );
  // scheduled_time round-trips through <input type="datetime-local">.
  // Convert the ISO instant (which Django stores in UTC) into the
  // browser's local wall-clock so the manager sees what they expect;
  // the inverse runs on submit. A truncate-to-16-chars approach
  // would silently shift times by the local-tz offset.
  const [scheduledTime, setScheduledTime] = useState(
    isoToLocalInputValue(
      summary.scheduled_time !== undefined
        ? summary.scheduled_time
        : before.scheduled_time
    )
  );

  // Issue-type options are snapshotted at resolution time for the
  // ticket's category, so any company-custom types are preserved.
  // Fallback to the standard set only if the snapshot is missing
  // (legacy proposal); the executor will reject anything outside the
  // company's configured options anyway.
  const {
    issueTypeOptions,
    priorityOptions,
    menuOptions,
    pending: categoryOptionsPending,
    status: optionsStatus,
    retry: retryCategoryOptions,
  } = useCategoryOptions({
    homeStore,
    category: ticketCategory,
    snapshotCategory: ticketCategory,
    snapshotIssueTypes: summary.issue_type_options,
    snapshotPriorities: summary.priority_options,
  });

  // Eligible-assignee list, category-scoped just like the create card.
  // ``ticket_store_id`` must be the proposal's target store (the
  // ticket already lives there) — falling back to ``homeStore`` would
  // pull eligible employees from the manager's session store instead,
  // and any selection there would fail the executor's
  // category-eligibility check at confirm time. ``store_id`` stays
  // homeStore because the endpoint authenticates the caller against
  // their session store.
  const ticketStoreId = proposal?.store_id || homeStore;
  const [employees, setEmployees] = useState([]);
  const fetchEmployees = useCallback(async () => {
    if (!homeStore || !ticketStoreId || !ticketCategory) return;
    try {
      const params = {
        store_id: homeStore,
        ticket_store_id: ticketStoreId,
        category: ticketCategory,
      };
      const res = await getRightworkAPI(API_get_store_employees_for_assignment, params);
      setEmployees(res.data?.redux?.payload?.employees || []);
    } catch {
      setEmployees([]);
    }
  }, [homeStore, ticketStoreId, ticketCategory]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleAction = (action) => {
    let overrides;
    if (action === "confirm") {
      overrides = {
        title: title.trim(),
        description: description.trim(),
        status: statusValue,
        priority,
      };
      // Menu tickets schedule a go-live, not a vendor visit, so they
      // never send vendor or scheduled_time.
      if (!isMenuTicket) {
        overrides.vendor = vendor.trim();
        // Empty string clears scheduled_time; non-empty is sent
        // as a tz-aware UTC ISO (Z-suffixed). Django's
        // parse_datetime preserves the timezone so the executor
        // stores the correct instant regardless of where the
        // manager's browser clock is set.
        overrides.scheduled_time = localInputValueToIso(scheduledTime);
      }
      // Only forward issue_type when the manager actually changed it.
      // Always-sending re-runs the executor's company-config check
      // against the existing value, which fails for legacy tickets
      // whose stored issue_type is no longer in the current custom
      // config — blocking unrelated edits.
      if (issueType !== initialIssueType) {
        overrides.issue_type = issueType;
      }
      if (JSON.stringify(equipment) !== JSON.stringify(initialEquipment)) {
        overrides.equipment_details = equipment;
      }
      if (ticketCategory === "menu" && JSON.stringify(menu) !== JSON.stringify(initialMenu)) {
        overrides.menu_details = menu;
      }
      // Only forward the assignee override when the manager actually
      // changed it. Always-sending would re-run the eligibility
      // validation against the existing (possibly now-archived or
      // permission-changed) assignee, blocking unrelated edits like
      // title/status/vendor. When the value is unchanged, omitting
      // the key keeps tool_args' assignee_employee_id at whatever
      // the LLM emitted (or absent if the LLM didn't touch it),
      // which lets the executor either re-validate the LLM's intent
      // OR skip assignee handling entirely — both correct.
      if (assigneeId !== initialAssigneeId) {
        overrides.assignee_employee_id = assigneeId
          ? Number(assigneeId)
          : null;
      }
    }
    return postConfirmAction({
      action,
      proposal,
      sessionUuid,
      homeStore,
      overrides,
      setStatus,
      setLoading,
      onStatusChange,
      errorMessage: "Failed to update ticket.",
    });
  };

  if (status === "confirmed") {
    return (
      <ConfirmedBanner
        icon={<CheckCircleIcon fontSize="small" className="text-green-600 mt-0.5 shrink-0" />}
        title={`Ticket #${before.ticket_number || summary.ticket_number || "?"} updated`}
        subtitle={title || summary.ticket_title || before.title}
      />
    );
  }
  if (status === "cancelled" || status === "rejected") {
    return <CancelledBanner label="Cancelled" />;
  }

  const confirmDisabled = loading || !title.trim() || !issueType || categoryOptionsPending;

  return (
    <div className="my-2 max-w-md rounded-lg border border-violet-200 bg-violet-50">
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200 rounded-t-lg">
        <EditNoteIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">
          Edit Ticket #{before.ticket_number || summary.ticket_number || "?"}
        </span>
      </div>
      <div className="px-3 py-2 space-y-2">
        <div className="text-[11px] text-gray-500">
          {labelFor(CATEGORY_OPTIONS, ticketCategory)}
        </div>

        <FormField label="Title">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={255}
            className={inputClasses}
          />
        </FormField>

        <FormField label="Description" optional>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className={`${inputClasses} resize-none`}
          />
        </FormField>

        <div className="flex gap-2">
          <FormField label="Issue type" className="flex-1">
            <select
              value={issueType}
              onChange={(e) => setIssueType(e.target.value)}
              className={inputClasses}
            >
              {issueTypeOptions.map((it) => (
                <option key={it.value} value={it.value}>
                  {it.label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Priority" className="flex-1" htmlFor={`ticket-priority-${proposal.proposal_id}`}>
            <RightworkSelect
              id={`ticket-priority-${proposal.proposal_id}`}
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              options={priorityOptions}
            />
          </FormField>
        </div>

        <AssetLine asset={summary.asset} previous={before.asset} />
        <RepairCostLines
          repairCost={summary.repair_cost}
          parts={summary.parts_used}
          previousCost={before.repair_cost}
          previousParts={before.parts_used}
        />

        <EquipmentFields
          equipment={equipment}
          setEquipment={setEquipment}
          show={Boolean(
            summary.equipment_details ||
            issueTypeOptions.find((it) => it.value === issueType)?.equipment_details
          )}
        />

        {ticketCategory === "menu" && (
          <MenuFields
            menu={menu}
            setMenu={setMenu}
            idPrefix={`ticket-menu-${proposal.proposal_id}`}
            menuOptions={menuOptions}
          />
        )}

        <div className="flex gap-2">
          <FormField label="Status" className="flex-1" htmlFor={`ticket-status-${proposal.proposal_id}`}>
            <RightworkSelect
              id={`ticket-status-${proposal.proposal_id}`}
              value={statusValue}
              onChange={(e) => setStatusValue(e.target.value)}
              options={statusOptions}
            />
          </FormField>
          <FormField label="Assignee" optional className="flex-1">
            <select
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              className={inputClasses}
            >
              <option value="">Unassigned</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.full_name ||
                    `${emp.first_name || ""} ${emp.last_name || ""}`.trim()}
                </option>
              ))}
            </select>
          </FormField>
        </div>

        {!isMenuTicket && (
          <>
            <FormField label="Vendor" optional>
              <input
                type="text"
                value={vendor}
                onChange={(e) => setVendor(e.target.value)}
                maxLength={255}
                className={inputClasses}
                placeholder="e.g. Acme Services"
              />
            </FormField>

            <FormField label="Scheduled time" optional>
              <input
                type="datetime-local"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className={inputClasses}
              />
            </FormField>
          </>
        )}
        {categoryOptionsPending && (
          <p className="text-xs text-amber-700">
            {optionsStatus === "error" ? (
              <>
                Couldn't load this ticket's options.{" "}
                <button type="button" className="underline font-medium" onClick={retryCategoryOptions}>
                  Retry
                </button>
              </>
            ) : (
              "Loading this ticket's options…"
            )}
          </p>
        )}
      </div>
      <CardFooter
        loading={loading}
        confirmDisabled={confirmDisabled}
        confirmLabel="Update ticket"
        onCancel={() => handleAction("reject")}
        onConfirm={() => handleAction("confirm")}
      />
    </div>
  );
}

// -- comment_on_ticket -----------------------------------------------------

function CommentTicketCard({
  proposal,
  summary,
  sessionUuid,
  homeStore,
  status,
  setStatus,
  loading,
  setLoading,
  onStatusChange,
}) {
  const [text, setText] = useState(summary.text || "");

  const handleAction = (action) => {
    const overrides =
      action === "confirm" && text.trim() !== (summary.text || "")
        ? { text: text.trim() }
        : undefined;
    return postConfirmAction({
      action,
      proposal,
      sessionUuid,
      homeStore,
      overrides,
      setStatus,
      setLoading,
      onStatusChange,
      errorMessage: "Failed to add comment.",
    });
  };

  if (status === "confirmed") {
    return (
      <ConfirmedBanner
        icon={<CheckCircleIcon fontSize="small" className="text-green-600 mt-0.5 shrink-0" />}
        title={`Comment added to #${summary.ticket_number || "?"}`}
        subtitle={summary.ticket_title}
      />
    );
  }
  if (status === "cancelled" || status === "rejected") {
    return <CancelledBanner label="Cancelled" />;
  }

  const confirmDisabled = loading || !text.trim() || text.length > 5000;

  return (
    <div className="my-2 max-w-md rounded-lg border border-violet-200 bg-violet-50">
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200 rounded-t-lg">
        <ChatBubbleOutlineIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">
          Comment on #{summary.ticket_number || "?"}
        </span>
      </div>
      <div className="px-3 py-2 space-y-2">
        {summary.ticket_title && (
          <div className="text-sm font-medium text-gray-800 truncate">
            {summary.ticket_title}
          </div>
        )}
        <div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={5000}
            rows={Math.min(Math.max(text.split("\n").length, 4), 8)}
            className={`${inputClasses} resize-none`}
            placeholder="Type your comment…"
          />
          <div
            className={`text-xs text-right mt-0.5 ${
              text.length >= 5000 ? "text-red-500" : "text-gray-400"
            }`}
          >
            {text.length} / 5000
          </div>
        </div>
        <div className="text-[11px] text-gray-500">
          The team will be notified in the {summary.category || "ticket"} chat channel.
        </div>
      </div>
      <CardFooter
        loading={loading}
        confirmDisabled={confirmDisabled}
        confirmLabel="Post comment"
        onCancel={() => handleAction("reject")}
        onConfirm={() => handleAction("confirm")}
      />
    </div>
  );
}

// -- Shared layout primitives ---------------------------------------------

const inputClasses =
  "w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white";

function FormField({ label, optional, className = "", htmlFor, children }) {
  return (
    <div className={className}>
      <label
        id={htmlFor ? `${htmlFor}-label` : undefined}
        htmlFor={htmlFor}
        className="block text-xs font-medium text-gray-600 mb-0.5"
      >
        {label}
        {optional && <span className="text-gray-400"> (optional)</span>}
      </label>
      {children}
    </div>
  );
}

// The equipment a ticket is linked to. Read-only here (same inline label
// style as the other cards' read-only values): the user asks the assistant
// to link a different one. On an edit, unlinking says what is removed.
function assetLabel(asset) {
  return [asset.code, asset.name, asset.location].filter(Boolean).join(" · ");
}

function ReadOnlyRow({ label, children }) {
  return (
    <div className="text-sm">
      <span className="text-gray-500">{label}: </span>
      <span className="font-medium text-gray-700">{children}</span>
    </div>
  );
}

function AssetLine({ asset, previous }) {
  if (asset) return <ReadOnlyRow label="Asset">{assetLabel(asset)}</ReadOnlyRow>;
  if (previous) return <ReadOnlyRow label="Asset">removed (was {assetLabel(previous)})</ReadOnlyRow>;
  return null;
}

function formatMoney(value) {
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "";
}

const hasValue = (value) => value !== null && value !== undefined && value !== "";

// "Thermostat (T-100) ×2 · $40.00": part number and the line's cost when
// recorded, so the cost can be checked before confirming.
function partLabel(p) {
  const number = p.part_number ? ` (${p.part_number})` : "";
  const cost = hasValue(p.cost) ? ` · ${formatMoney(p.cost)}` : "";
  return `${p.name}${number} ×${p.quantity || 1}${cost}`;
}

// Repair cost and parts as the ticket will hold them (a $0 cost included).
// Read-only here; on an edit, clearing a recorded value says what is removed.
function RepairCostLines({ repairCost, parts, previousCost, previousParts }) {
  const partList = (parts || []).filter((p) => p?.name);
  const previousPartList = (previousParts || []).filter((p) => p?.name);
  return (
    <>
      {hasValue(repairCost) ? (
        <ReadOnlyRow label="Repair cost">{formatMoney(repairCost)}</ReadOnlyRow>
      ) : hasValue(previousCost) ? (
        <ReadOnlyRow label="Repair cost">removed (was {formatMoney(previousCost)})</ReadOnlyRow>
      ) : null}
      {partList.length ? (
        <ReadOnlyRow label="Parts">{partList.map(partLabel).join(", ")}</ReadOnlyRow>
      ) : previousPartList.length ? (
        <ReadOnlyRow label="Parts">removed (was {previousPartList.map(partLabel).join(", ")})</ReadOnlyRow>
      ) : null}
    </>
  );
}

function CardFooter({ loading, confirmDisabled, confirmLabel, onCancel, onConfirm }) {
  return (
    <div className="flex justify-end gap-2 px-3 py-2 border-t border-violet-200 bg-white rounded-b-lg">
      <button
        onClick={onCancel}
        disabled={loading}
        className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        onClick={onConfirm}
        disabled={confirmDisabled}
        className="px-3 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-1"
      >
        {loading && <CircularProgress size={14} sx={{ color: "white" }} />}
        {confirmLabel}
      </button>
    </div>
  );
}

function ConfirmedBanner({ icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 my-2">
      {icon}
      <div className="min-w-0">
        <div className="font-medium">{title}</div>
        {subtitle && <div className="text-xs opacity-80 truncate">{subtitle}</div>}
      </div>
    </div>
  );
}

function CancelledBanner({ label }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500 my-2">
      <CancelIcon fontSize="small" />
      <span>{label}</span>
    </div>
  );
}

function labelFor(options, value) {
  const found = options.find((o) => o.value === value);
  return found ? found.label : value || "—";
}
